/**
 * DeletePayAppDialog — draft simple confirm; submitted-unpaid type DELETE; paid blocked.
 */

import { useState, type FormEvent } from 'react'
import {
  deletePayApp,
  type DeletePayAppWarning,
} from '../../../api/payApps'
import type { PayApp } from '../../../api/types'

interface Props {
  payApp: PayApp
  open: boolean
  onClose: () => void
  onDeleted: () => void
}

function isPaid(pa: PayApp): boolean {
  return pa.payment_status === 'paid' || pa.status === 'paid'
}

function needsTypeConfirm(pa: PayApp): boolean {
  if (pa.status === 'draft') return false
  return !isPaid(pa)
}

export default function DeletePayAppDialog({ payApp, open, onClose, onDeleted }: Props) {
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [confirmText, setConfirmText] = useState('')
  const [cascadeWarning, setCascadeWarning] = useState<DeletePayAppWarning | null>(null)

  if (!open) return null

  const paid = isPaid(payApp)
  const typeConfirm = needsTypeConfirm(payApp)
  const canDelete = !paid && (!typeConfirm || confirmText.trim().toUpperCase() === 'DELETE')

  async function runDelete(cascade = false) {
    setBusy(true)
    setErr(null)
    try {
      const res = await deletePayApp(payApp.id, cascade)
      if ('warning' in res && res.warning) {
        setCascadeWarning(res)
        return
      }
      onDeleted()
      onClose()
    } catch (ex) {
      setErr(ex instanceof Error ? ex.message : 'Delete failed')
    } finally {
      setBusy(false)
    }
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    if (!canDelete || busy) return
    void runDelete(!!cascadeWarning)
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="delete-payapp-dialog"
      style={{
        position: 'fixed', inset: 0, zIndex: 9000,
        background: 'rgba(0,0,0,.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16,
      }}
      onClick={onClose}
    >
      <form
        onSubmit={handleSubmit}
        onClick={e => e.stopPropagation()}
        style={{
          background: 'var(--surface)',
          borderRadius: 'var(--rl)',
          padding: 20,
          maxWidth: 440,
          width: '100%',
          boxShadow: '0 12px 40px rgba(0,0,0,.2)',
        }}
      >
        <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink)', marginBottom: 6 }}>
          Delete Pay App #{payApp.app_number}?
        </div>

        {paid ? (
          <p style={{ fontSize: 12, color: 'var(--ink2)', lineHeight: 1.5, marginBottom: 14 }}>
            This pay application has been paid and cannot be deleted.
          </p>
        ) : cascadeWarning ? (
          <>
            <p style={{ fontSize: 12, color: 'var(--ink2)', lineHeight: 1.5, marginBottom: 10 }}>
              {cascadeWarning.message}
            </p>
            <ul style={{ fontSize: 11, color: 'var(--ink3)', marginBottom: 12, paddingLeft: 18 }}>
              {cascadeWarning.subsequent.map(s => (
                <li key={s.id}>PA #{s.app_number} — {s.period_label || s.status}</li>
              ))}
            </ul>
            <p style={{ fontSize: 11, color: 'var(--red)', marginBottom: 12 }}>
              Deleting will also remove all subsequent pay apps listed above.
            </p>
          </>
        ) : (
          <p style={{ fontSize: 12, color: 'var(--ink2)', lineHeight: 1.5, marginBottom: 14 }}>
            {typeConfirm
              ? 'This pay app was submitted. Type DELETE below to confirm removal.'
              : 'Remove this draft pay application? Line items and change orders will be hidden.'}
          </p>
        )}

        {typeConfirm && !paid && !cascadeWarning && (
          <input
            className="fld-i"
            value={confirmText}
            onChange={e => setConfirmText(e.target.value)}
            placeholder="Type DELETE"
            data-testid="delete-payapp-confirm-input"
            style={{ width: '100%', marginBottom: 12 }}
            autoFocus
          />
        )}

        {err && (
          <div style={{ color: 'var(--red)', fontSize: 11, marginBottom: 10 }}>{err}</div>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button type="button" className="btn b-ol btn-sm" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          {!paid && (
            <button
              type="submit"
              className="btn b-red btn-sm"
              disabled={!canDelete || busy}
              data-testid="delete-payapp-confirm-btn"
            >
              {busy ? 'Deleting…' : cascadeWarning ? 'Delete all listed' : 'Delete pay app'}
            </button>
          )}
        </div>
      </form>
    </div>
  )
}
